import React, { useState, useEffect, useMemo, useCallback } from "react";
import { AxiosError } from "axios";
import { EmailEdit } from "../../client/activity/components/EmailEdit";
import AIAgentEvaluatePopup from "../../client/activity/components/AIAgentEvaluatePopup";
import { useWindow } from "../../../components/windows/WindowContext";
import { TranslationService } from "../../../services/TranslationService";
import ActivityService from "../../../services/ActivityService";
import AssistantService from "../../../services/AssistantService";
import EmailTemplateService from "../../../services/EmailTemplateService";
import CompanyService from "../../../services/CompanyService";
import { ToastService } from "../bootstrap/Toast";
import Loading from "./Loading";
import "./floatingStyles.css";

/* ── Tipos ── */

export type EmailAICopilotPanelProps = {
  emailId: number;
  personId: number;
  emailData: any;
  actions: any[];
  reload: () => void;
  onCopilotComplete?: () => void;
};

export type EmailAICopilotPanelWithLoadingProps = Omit<
  EmailAICopilotPanelProps,
  "emailData"
>;

type AIEvaluateWithLoadingProps = {
  emailId: number;
  personId: number;
  onComplete?: () => void;
  onCancel?: () => void;
};

/* ── AIEvaluatePopupWithLoading unificado ── */

const AIEvaluatePopupWithLoading: React.FC<AIEvaluateWithLoadingProps> = ({
  emailId,
  personId,
  onComplete,
  onCancel
}) => {
  const [loading, setLoading] = useState(true);
  const [aiData, setAiData] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { translate } = TranslationService;

  useEffect(() => {
    const load = async () => {
      try {
        const result = await AssistantService.aicategorize(emailId);
        if (result instanceof Error) {
          const axiosError = result as AxiosError<{ error?: string }>;
          const message =
            axiosError.response?.data?.error === "insufficient_balance"
              ? translate.AIInsufficientBalance
              : translate.ErrorProcessingRequest;
          ToastService.showToast(message, undefined, "danger");
          setErrorMessage(message);
          setLoading(false);
          return;
        }
        setAiData(result);
      } catch {
        ToastService.showToast(
          translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
        setErrorMessage(translate.ErrorProcessingRequest);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [emailId, translate]);

  const handleConfirm = useCallback(
    async (json: any) => {
      try {
        const result = await AssistantService.setAICategorized(
          personId,
          json,
          emailId
        );
        if (result instanceof Error) {
          ToastService.showToast(
            translate.ErrorProcessingRequest,
            undefined,
            "danger"
          );
          return;
        }
        ToastService.showToast(
          translate.ActionCompletedSuccessfully,
          undefined,
          "success"
        );
        onComplete?.();
      } catch {
        ToastService.showToast(
          translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
      }
    },
    [emailId, personId, translate, onComplete]
  );

  const resolveTemplate = useCallback(async (templateId: string) => {
    try {
      const id = parseInt(templateId, 10);
      if (isNaN(id)) return null;
      const result = await EmailTemplateService.get(id);
      if (result instanceof Error) return null;
      const t = result.item;
      return {
        id: t.MailTemplateID.toString(),
        name: t.Name,
        subject: t.Subject,
        body: t.Content || "",
        includesPendingList: false
      };
    } catch {
      return null;
    }
  }, []);

  const resolveUserName = useCallback((responsible: string) => {
    const lower = String(responsible || "").toLowerCase();
    const user = CompanyService.getUsers()?.find(
      (u) => u.Value?.toLowerCase() === lower || String(u.Id) === lower
    );
    return user?.Value || null;
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: 200
        }}
      >
        <Loading />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
          minHeight: 200,
          padding: 24,
          color: "#4b5563"
        }}
      >
        {errorMessage}
      </div>
    );
  }

  if (!aiData) return null;

  return (
    <AIAgentEvaluatePopup
      json={{ ...aiData, emailId }}
      personId={personId}
      resolveTemplate={resolveTemplate}
      resolveUserName={resolveUserName}
      onConfirm={handleConfirm}
      onCancel={onCancel}
    />
  );
};

/* ── Panel combinado (visual) ── */

export const EmailAICopilotPanel: React.FC<EmailAICopilotPanelProps> = ({
  emailId,
  personId,
  emailData,
  actions,
  reload,
  onCopilotComplete
}) => {
  const { close, minimize } = useWindow();
  const [aiKey, setAiKey] = useState(0);
  const { translate } = TranslationService;

  const actionsWithAIReload = useMemo(
    () =>
      actions.map((a) =>
        a.icon === "emoji:✨"
          ? { ...a, onClick: () => setAiKey((k) => k + 1) }
          : a
      ),
    [actions]
  );

  const handleCopilotComplete = useCallback(() => {
    onCopilotComplete?.();
    close();
  }, [onCopilotComplete, close]);

  return (
    <div style={{ display: "flex", height: "100%" }}>
      {/* ── Columna Email (54%) ── */}
      <div
        style={{
          flex: "0 0 54%",
          display: "flex",
          flexDirection: "column",
          borderRight: "1px solid #e6e8ec",
          height: "100%",
          minWidth: 0
        }}
      >
        <div
          className="win-email-header"
          style={{
            flexShrink: 0,
            height: 48,
            background: "#20304f",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            padding: "0 18px",
            cursor: "move",
            userSelect: "none"
          }}
        >
          <span
            style={{
              fontWeight: 500,
              fontSize: 13.5,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {emailData.Subject}
          </span>
        </div>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <EmailEdit
            email={emailData}
            onSubmit={reload}
            actions={actionsWithAIReload}
          />
        </div>
      </div>

      {/* ── Columna IA (flex: 1) ── */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minWidth: 0,
          background: "linear-gradient(180deg, #eef0fc, #e6e8f7)"
        }}
      >
        <div
          style={{
            flexShrink: 0,
            height: 48,
            background: "linear-gradient(90deg, #4f46e5, #7c3aed)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            padding: "0 8px 0 20px",
            gap: 10
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              fontWeight: 600,
              fontSize: 13.5,
              flex: 1,
              minWidth: 0
            }}
          >
            <span style={{ fontSize: 15, flexShrink: 0 }}>✨</span>
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
            >
              {translate.Recommendations}
            </span>
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: ".4px",
              textTransform: "uppercase",
              color: "#fff",
              background: "rgba(255,255,255,.2)",
              border: "1px solid rgba(255,255,255,.4)",
              borderRadius: 20,
              padding: "3px 9px",
              flexShrink: 0,
              whiteSpace: "nowrap"
            }}
          >
            Agente IA
          </span>
          <span
            className="buttonContainer"
            style={{
              display: "flex",
              gap: 8,
              marginRight: 8,
              cursor: "pointer",
              fontSize: "20px"
            }}
          >
            <span className="windowButton" onClick={minimize}>
              <i className="fas fa-window-minimize" />
            </span>
            <span className="windowButton" onClick={close}>
              <i className="fas fa-window-close" />
            </span>
          </span>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column"
          }}
        >
          <AIEvaluatePopupWithLoading
            key={aiKey}
            emailId={emailId}
            personId={personId}
            onComplete={handleCopilotComplete}
            onCancel={close}
          />
        </div>
      </div>
    </div>
  );
};

/* ── Wrapper con carga del email ── */

export const EmailAICopilotPanelWithLoading: React.FC<
  EmailAICopilotPanelWithLoadingProps
> = ({ emailId, personId, actions, reload, onCopilotComplete }) => {
  const [loading, setLoading] = useState(true);
  const [emailData, setEmailData] = useState<any>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await ActivityService.get(emailId);
        await ActivityService.setRead({ ids: [emailId], read: "1" });
        if (!(res instanceof Error)) setEmailData(res.item);
      } catch {
        // error manejado por los servicios
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [emailId]);

  if (loading) return <Loading height={400} />;
  if (!emailData) return null;

  return (
    <EmailAICopilotPanel
      emailId={emailId}
      personId={personId}
      emailData={emailData}
      actions={actions}
      reload={reload}
      onCopilotComplete={onCopilotComplete}
    />
  );
};
